// ResourcePreloader - Intelligentes Preloading mit Prioritäten
class ResourcePreloader {
  constructor() {
    this.queue = [];
    this.loaded = new Set();
    this.loading = new Set();
    this.observers = new Map();
    this.isIdle = false;

    // Überprüfe ob Browser Idle Callback unterstützt
    this.supportsIdleCallback = 'requestIdleCallback' in window;

    // Device Detection
    this.isMobile = this.detectMobile();
    this.connectionQuality = this.detectConnection();
    this.maxConcurrent = this.getMaxConcurrent();

    // Starte Idle Detection
    this.setupIdleDetection();
  }

  // Detect Mobile Device
  detectMobile() {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth < 768;
  }

  // Detect Connection Quality
  detectConnection() {
    if (typeof navigator === 'undefined' || !navigator.connection) {
      return this.isMobile ? 'medium' : 'fast';
    }

    const effectiveType = navigator.connection.effectiveType;
    if (effectiveType === '4g' || effectiveType === 'wifi') return 'fast';
    if (effectiveType === '3g') return 'medium';
    return 'slow';
  }

  // Get Max Concurrent based on device
  getMaxConcurrent() {
    if (this.isMobile) return 3; // Mobile: max 3
    if (this.connectionQuality === 'slow') return 2;
    if (this.connectionQuality === 'medium') return 4;
    return 6; // Desktop/Fast: max 6
  }

  // Prioritätsstufen
  static PRIORITY = {
    CRITICAL: 1,    // Sofort laden (Hero Section, Above the Fold)
    HIGH: 2,        // Schnell laden (wichtige UI Elemente)
    MEDIUM: 3,      // Normal laden (Rest der aktuellen Seite)
    LOW: 4,         // Im Hintergrund (andere Seiten)
    IDLE: 5         // Nur wenn Browser idle ist
  };

  // Füge Ressource zur Warteschlange hinzu
  addResource(url, priority = ResourcePreloader.PRIORITY.MEDIUM, type = 'image') {
    if (this.loaded.has(url) || this.loading.has(url)) {
      return Promise.resolve();
    }

    const resource = {
      url,
      priority,
      type,
      promise: null,
      resolve: null,
      reject: null
    };

    // Erstelle Promise für diese Ressource
    resource.promise = new Promise((resolve, reject) => {
      resource.resolve = resolve;
      resource.reject = reject;
    });

    // Füge zur Queue hinzu und sortiere
    this.queue.push(resource);
    this.queue.sort((a, b) => a.priority - b.priority);

    // Starte Ladevorgang
    this.processQueue();

    return resource.promise;
  }

  // Verarbeite Warteschlange
  processQueue() {
    // Nutze dynamisches Limit basierend auf Gerät
    const MAX_CONCURRENT = this.maxConcurrent;

    while (this.loading.size < MAX_CONCURRENT && this.queue.length > 0) {
      const resource = this.queue.shift();

      // Überspringe niedrige Prioritäten wenn nicht idle
      if (resource.priority >= ResourcePreloader.PRIORITY.IDLE && !this.isIdle) {
        this.queue.push(resource); // Zurück in die Queue
        break;
      }

      // Auf Mobile: Überspringe LOW priority wenn bereits etwas lädt
      if (this.isMobile && resource.priority >= ResourcePreloader.PRIORITY.LOW && this.loading.size > 0) {
        this.queue.push(resource);
        break;
      }

      this.loadResource(resource);
    }
  }

  // Lade einzelne Ressource
  async loadResource(resource) {
    this.loading.add(resource.url);

    try {
      let result;

      switch (resource.type) {
        case 'image':
          result = await this.preloadImage(resource.url);
          break;
        case 'video':
          result = await this.preloadVideo(resource.url);
          break;
        case 'script':
          result = await this.preloadScript(resource.url);
          break;
        case 'style':
          result = await this.preloadStyle(resource.url);
          break;
        case 'fetch':
          result = await this.preloadFetch(resource.url);
          break;
        default:
          result = await this.preloadImage(resource.url);
      }

      this.loaded.add(resource.url);
      resource.resolve(result);
    } catch (error) {
      // Nur Debug-Level logging für fehlgeschlagene Preloads
      if (process.env.NODE_ENV === 'development') {
        console.debug(`Preload skipped for ${resource.url}`);
      }
      // Markiere trotzdem als "geladen" um erneute Versuche zu vermeiden
      this.loaded.add(resource.url);
      resource.resolve(null);
    } finally {
      this.loading.delete(resource.url);
      // Verarbeite nächste Ressource
      this.processQueue();
    }
  }

  // Preload Image
  preloadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => {
        // Stille Fehlerbehandlung - keine Console Spam
        reject(e);
      };
      img.src = url;
    });
  }

  // Preload Video
  preloadVideo(url) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;

      const handleLoad = () => {
        // Video ist genug geladen
        resolve(video);
      };

      // Nutze canplaythrough als Indikator dass genug gepuffert wurde
      video.addEventListener('canplaythrough', handleLoad, { once: true });
      video.addEventListener('error', reject, { once: true });

      // Setze Source
      video.src = url;

      // Starte das Laden
      video.load();
    });
  }

  // Preload Script
  preloadScript(url) {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = url;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  // Preload Stylesheet
  preloadStyle(url) {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'style';
      link.href = url;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  // Preload via Fetch (für API Calls)
  async preloadFetch(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response;
  }

  // Setup Idle Detection
  setupIdleDetection() {
    if (this.supportsIdleCallback) {
      const checkIdle = () => {
        requestIdleCallback((deadline) => {
          this.isIdle = deadline.timeRemaining() > 0;

          if (this.isIdle) {
            this.processQueue(); // Verarbeite niedrige Prioritäten
          }

          // Wiederhole Check
          setTimeout(checkIdle, 1000);
        });
      };
      checkIdle();
    } else {
      // Fallback: Nutze setTimeout
      setTimeout(() => {
        this.isIdle = true;
        this.processQueue();
      }, 3000);
    }
  }

  // Preload für bestimmte Route
  preloadRoute(routeAssets) {
    routeAssets.forEach(asset => {
      this.addResource(asset.url, asset.priority, asset.type);
    });
  }

  // Preload kritische Ressourcen (Hero Section)
  preloadCritical(urls) {
    urls.forEach(url => {
      this.addResource(url, ResourcePreloader.PRIORITY.CRITICAL);
    });
  }

  // Observer für Intersection (Lazy Loading)
  observeElement(element, callback, options = {}) {
    const defaultOptions = {
      root: null,
      rootMargin: '50px', // 50px vor dem Viewport beginnen zu laden
      threshold: 0.01
    };

    const observerOptions = { ...defaultOptions, ...options };

    if (!this.observers.has(JSON.stringify(observerOptions))) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const callback = entry.target._callback;
            if (callback) {
              callback();
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      this.observers.set(JSON.stringify(observerOptions), observer);
    }

    const observer = this.observers.get(JSON.stringify(observerOptions));
    element._callback = callback;
    observer.observe(element);
  }

  // Cleanup
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.queue = [];
    this.loading.clear();
  }

  // Check if resource is loaded
  isLoaded(url) {
    return this.loaded.has(url);
  }

  // Get loading status
  getStatus() {
    return {
      queued: this.queue.length,
      loading: this.loading.size,
      loaded: this.loaded.size,
      isIdle: this.isIdle
    };
  }
}

// Singleton Instance
const preloader = new ResourcePreloader();

// Route Assets Definition - Mit allen lokalen Videos und Bildern
export const ROUTE_ASSETS = {
  home: [
    // Hero Section - CRITICAL (lädt sofort)
    { url: '/img/BusDriver.svg', priority: ResourcePreloader.PRIORITY.CRITICAL, type: 'image' },
    { url: '/header-animation_v4.webm', priority: ResourcePreloader.PRIORITY.CRITICAL, type: 'video' },

    // Header Images - HIGH (schnell laden)
    { url: '/img/BusAgentTrans.png', priority: ResourcePreloader.PRIORITY.HIGH, type: 'image' },
    { url: '/img/Bus.svg', priority: ResourcePreloader.PRIORITY.HIGH, type: 'image' },
    { url: '/img/BusGuy.svg', priority: ResourcePreloader.PRIORITY.HIGH, type: 'image' },
    { url: '/img/BusShield.svg', priority: ResourcePreloader.PRIORITY.HIGH, type: 'image' },

    // PlatformSection Videos - HIGH (near the fold, wichtige UI)
    { url: '/videos/Platform.mp4', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
    { url: '/videos/Angebotserstellung.mp4', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
    { url: '/videos/Umlauf.mp4', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },

    // Section Images - MEDIUM
    { url: '/img/bow.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/teamTalk.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
  ],

  angebotserstellung: [
    { url: '/img/offerBus.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
  ],

  lohnabrechnung: [
    // Lokale Videos
    { url: '/videos/lohnDashboard.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
    { url: '/videos/lohnDaten.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ],

  umlauf: [
    { url: '/img/square.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    // Video
    { url: '/videos/Disposition.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ],

  disposition: [
    { url: '/img/doubleL.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/personOnComputer.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    // Videos
    { url: '/videos/Disposition.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
    { url: '/videos/KrankMeldung.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ],

  fehlermeldung: [
    { url: '/img/QRcode.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/ErrorScreen.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/errorStart.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/doubleSticks.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    // Video
    { url: '/videos/Werkstatt.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ],

  betriebskosten: [
    { url: '/img/BusAgentTrans.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    // Video
    { url: '/videos/GeldSparen.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ],

  fahrpersonal: [
    { url: '/img/happyBusDriver.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
  ],

  fahrgastzufriedenheit: [
    { url: '/img/womanPassenger.jpg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/Gästewarten.jpg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
  ],

  ueberuns: [
    { url: '/img/gerCard.svg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/founders/Daniel.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/founders/Fabian.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/founders/Moritz.png', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/CityImage.webp', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/happyWoman.jpg', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    { url: '/img/nachhaltigerBus.webp', priority: ResourcePreloader.PRIORITY.MEDIUM, type: 'image' },
    // Video mit hoher Priorität für Über uns Seite
    { url: '/videos/BusAgentTeam.webm', priority: ResourcePreloader.PRIORITY.HIGH, type: 'video' },
  ]
};

export { ResourcePreloader, preloader };